import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarClock } from "lucide-react";

import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import CalendlyInline from "@/components/site/CalendlyInline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BookPage() {
  const [calendlyUrl, setCalendlyUrl] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [searchParams] = useSearchParams();
  const refId = (searchParams.get("ref") || "").trim() || null;

  useEffect(() => {
    let alive = true;
    axios
      .get(`${BACKEND_URL}/api/site-config`, { timeout: 8000 })
      .then((r) => {
        if (!alive) return;
        setCalendlyUrl(r?.data?.calendly_url || null);
        setLoaded(true);
      })
      .catch(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    document.title = "Book a Call — The Modern Exit";
  }, []);

  return (
    <div className="bg-sand-100 min-h-screen text-ink">
      <Nav />

      <main
        data-testid="book-page"
        className="px-5 sm:px-6 md:px-10 pt-28 sm:pt-32 pb-16 sm:pb-24"
      >
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="max-w-3xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay flex items-center gap-2">
              <CalendarClock size={12} /> Book a call
            </p>
            <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-ink leading-[1.05] tracking-tight">
              Pick a window. We'll meet you there.
            </h1>
            <p className="mt-5 text-[15px] sm:text-base text-ink-muted leading-relaxed max-w-2xl">
              A 30-minute working session — no slides, no pitch deck. Bring
              the bottleneck and we'll map the highest-leverage move you can
              make in the next 30 days.
            </p>
          </div>

          {/* Embed */}
          <div className="mt-10 sm:mt-12 rounded-3xl border border-ink/10 bg-sand-50 p-2 sm:p-3">
            {loaded ? (
              <CalendlyInline
                url={calendlyUrl}
                refId={refId}
                minHeight={720}
                className="rounded-2xl overflow-hidden"
              />
            ) : (
              <div
                data-testid="book-loading"
                className="h-[720px] flex items-center justify-center"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                  Loading scheduler…
                </p>
              </div>
            )}
          </div>

          {/* Secondary actions */}
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <Link
              to="/"
              data-testid="book-back-home"
              className="inline-flex items-center gap-2 text-[13px] text-ink-muted hover:text-clay transition-colors"
            >
              <ArrowLeft size={13} /> Back to home
            </Link>
            <span className="hidden sm:inline text-ink-muted/40">·</span>
            <Link
              to="/intake"
              data-testid="book-prefer-form"
              className="text-[13px] text-ink-muted hover:text-clay transition-colors"
            >
              Prefer to send details first? Use the intake form.
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
